import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { apiGet } from "@/lib/api";
import { BarChart } from "@/components/reports/bar-chart";

export const Route = createFileRoute("/_authenticated/reports/hazards")({
  component: HazardsReport,
});

interface HazardRow {
  hazardLabel: string;
  category: string;
  count: number;
}

const CATEGORY_COLORS: Record<string, string> = {
  physical: "bg-red-500",
  chemical: "bg-purple-500",
  biological: "bg-green-500",
  ergonomic: "bg-yellow-500",
  environmental: "bg-blue-500",
  psychosocial: "bg-pink-500",
};

function daysAgo(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

function HazardsReport() {
  const params = new URLSearchParams(window.location.search);
  const dateFrom = params.get("dateFrom") || daysAgo(30);
  const dateTo = params.get("dateTo") || today();
  const projectId = params.get("projectId") || undefined;

  const qs = new URLSearchParams({ dateFrom, dateTo });
  if (projectId) qs.set("projectId", projectId);

  const { data, isLoading } = useQuery({
    queryKey: ["reports", "hazard-frequency", { dateFrom, dateTo, projectId }],
    queryFn: () =>
      apiGet<{ data: HazardRow[] }>(
        `/api/reports/hazard-frequency?${qs.toString()}`
      ),
  });

  if (isLoading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="h-10 animate-pulse bg-muted/40 rounded" />
        ))}
      </div>
    );
  }

  const hazards = data?.data ?? [];

  const items = hazards.map((h) => ({
    label: h.hazardLabel,
    value: h.count,
    sub: h.category,
    color: CATEGORY_COLORS[h.category?.toLowerCase()] || "bg-blue-500",
  }));

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
          Top 20 Hazards by Frequency
        </h3>
        <span className="text-xs text-muted-foreground">
          {hazards.length} hazard type{hazards.length !== 1 ? "s" : ""}
        </span>
      </div>
      <BarChart
        items={items}
        emptyMessage="No hazards recorded for the selected period."
      />
    </div>
  );
}
